import { useAuthStore } from '@/stores/authStore';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CircleDot, Calendar, Baby, BookOpen, Users, Heart, School } from 'lucide-react';
import { tenantAuditLogsApi, dashboardApi } from '@/services/api';
import type { TenantAuditLog, BehaviorAuditLogsResponse } from '@/services/api';
import { StatCard } from '@/components/admin/StatCard';
import { QuickActions } from '@/components/admin/QuickActions';
import { DataCompletenessCard } from '@/components/admin/DataCompletenessCard';
import { useSchoolTimezone } from '@/contexts/TimezoneContext';
import { useSchedulingTerminology } from '@/hooks/useSchedulingTerminology';
import { formatDate, getRelativeTime } from '@/utils/dateFormatting';
import { useNameFormat } from '@/utils/nameFormat';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getContainerStyle } from '@/styles/containers';

const DashboardPage = () => {
  usePageTitle('Dashboard');
  const { user, tenant } = useAuthStore();
  const { timezone } = useSchoolTimezone();
  const { isMobile } = useMediaQuery();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const { formatStaffName, formatStudentName } = useNameFormat();

  // Get dynamic terminology based on school's scheduling mode
  const schedulingMode = tenant?.settings?.schedulingUiMode || 'both';
  const terminology = useSchedulingTerminology(schedulingMode);

  // Fetch dashboard stats (admin only)
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await dashboardApi.getStats();
      return response.data;
    },
    enabled: isAdmin,
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch recent audit activity (admin only)
  const { data: recentActivity } = useQuery<{ success: boolean; data: TenantAuditLog[] }>({
    queryKey: ['tenant-audit-recent'],
    queryFn: () => tenantAuditLogsApi.getRecentActivity(10), // Increased to 10 items
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  // Fetch recent behavior audit activity (admin only)
  const { data: recentBehaviors, isLoading: behaviorsLoading } = useQuery<BehaviorAuditLogsResponse>({
    queryKey: ['recent-behavior-audit'],
    queryFn: () => tenantAuditLogsApi.getBehaviorAuditLogs({ limit: 10 }),
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  const containerStyle = getContainerStyle('full', isMobile);

  const headerStyle: React.CSSProperties = {
    marginBottom: '1rem',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '0.25rem',
  };

  const subtitleStyle: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontSize: '0.875rem',
  };

  const statusBarStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    fontSize: '0.75rem',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.625rem',
    marginBottom: '1rem',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--background-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '1rem',
    boxShadow: 'var(--shadow)',
    marginBottom: '0.75rem',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: '600',
    marginBottom: '0.75rem',
    color: 'var(--text-primary)',
  };

  const linkButtonStyle: React.CSSProperties = {
    display: 'inline-block',
    marginTop: '0.75rem',
    padding: '0.5rem 0.875rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: 'var(--radius)',
    fontSize: '0.8125rem',
    fontWeight: '500',
    transition: 'all 0.2s ease-in-out',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8125rem',
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '0.375rem 0.5rem',
    borderBottom: '2px solid var(--border-color)',
    color: 'var(--text-secondary)',
    fontWeight: 500,
    fontSize: '0.75rem',
  };

  const tdStyle: React.CSSProperties = {
    padding: '0.375rem 0.5rem',
    borderBottom: '1px solid var(--border-color)',
  };

  const truncatedTextStyle: React.CSSProperties = {
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const getActionColor = (action: string): string => {
    switch (action) {
      case 'created':
        return '#10b981';
      case 'updated':
        return '#3b82f6';
      case 'deleted':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  return (
    <div style={containerStyle}>
      {/* Welcome Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Welcome back, {user?.firstName}!</h1>
        <p style={subtitleStyle}>{tenant ? `${tenant.schoolName} Dashboard` : 'Dashboard'}</p>
      </div>

      {/* System Status Bar */}
      {stats?.schoolYear && (
        <div style={statusBarStyle}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <CircleDot size={14} aria-hidden="true" style={{ color: 'var(--success-color)' }} /> System Healthy
          </span>
          <span>|</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <Calendar size={14} aria-hidden="true" /> {stats.schoolYear.name} ({formatDate(stats.schoolYear.startDate)}{' '}
            - {formatDate(stats.schoolYear.endDate)})
          </span>
        </div>
      )}

      {/* Quick Actions (Admin Only) */}
      {isAdmin && <QuickActions />}

      {/* Statistics Grid (Admin Only) */}
      {isAdmin && (
        <div style={gridStyle}>
          <StatCard
            Icon={School}
            label="School Year"
            value={stats?.schoolYear?.name || 'Not Set'}
            subtext={
              stats?.schoolYear
                ? `${formatDate(stats.schoolYear.startDate)} - ${formatDate(stats.schoolYear.endDate)}`
                : undefined
            }
            linkTo="/admin/school-years"
            loading={statsLoading}
          />
          <StatCard
            Icon={Baby}
            label="Total Students"
            value={stats?.students.total || 0}
            linkTo="/admin/students"
            loading={statsLoading}
          />
          <StatCard
            Icon={Users}
            label="Total Staff"
            value={stats?.staff.total || 0}
            linkTo="/admin/staff"
            loading={statsLoading}
          />
          <StatCard
            Icon={Heart}
            label="Total Family Members"
            value={stats?.parents.total || 0}
            linkTo="/admin/parents"
            loading={statsLoading}
          />
          {schedulingMode !== 'traditional' && (
            <StatCard
              Icon={BookOpen}
              label="Total Classes"
              value={stats?.classes.total || 0}
              linkTo="/admin/classes"
              loading={statsLoading}
            />
          )}
          <StatCard
            Icon={Calendar}
            label={terminology.totalBlocksLabel}
            value={stats?.blocks.total || 0}
            linkTo={terminology.blocksPageLink}
            loading={statsLoading}
          />
        </div>
      )}

      {/* Data Completeness Warnings (Admin Only) */}
      {isAdmin && stats && (
        <>
          {stats.dataCompleteness.classesWithoutMeetingPatterns > 0 && (
            <DataCompletenessCard
              type="warning"
              count={stats.dataCompleteness.classesWithoutMeetingPatterns}
              entity="classes"
              issue="missing meeting patterns"
              description="Classes without meeting patterns cannot appear on teacher schedules. Add patterns to make them visible."
              actionLink="/admin/classes"
            />
          )}

          {stats.dataCompleteness.studentsWithoutEnrollment > 0 && (
            <DataCompletenessCard
              type="warning"
              count={stats.dataCompleteness.studentsWithoutEnrollment}
              entity="students"
              issue="without enrollments"
              description="Students must be enrolled in at least one class to appear on rosters."
              actionLink="/admin/students?filter=no-enrollments"
              actionLabel="View Students"
            />
          )}

          {stats.dataCompleteness.blocksWithoutPatterns > 0 && (
            <DataCompletenessCard
              type="info"
              count={stats.dataCompleteness.blocksWithoutPatterns}
              entity={terminology.blocksLabel.toLowerCase()}
              issue="without meeting patterns"
              description={`${terminology.blocksLabel} without meeting patterns won't appear on teacher schedules. Add meeting patterns to make them visible.`}
              actionLink={terminology.blocksPageLink}
            />
          )}
        </>
      )}

      {/* Activity Panels Grid (Admin only) */}
      {isAdmin && (recentBehaviors?.logs?.length || recentActivity?.data?.length) && (() => {
        // Determine grid columns based on which panels have data
        const hasBehaviors = recentBehaviors?.logs && recentBehaviors.logs.length > 0;
        const hasActivity = recentActivity?.data && recentActivity.data.length > 0;
        const gridCols = isMobile || (hasBehaviors !== hasActivity) ? '1fr' : '1fr 1fr';

        return (
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '0.75rem' }}>
            {/* Recent Behavior Activity Panel (LEFT) */}
            {hasBehaviors && (
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Recent Behavior Activity</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                  Latest behavior records logged by staff
                </p>
                {behaviorsLoading ? (
                  <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>Loading...</div>
                ) : (
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>Student</th>
                        <th style={thStyle}>Teacher</th>
                        <th style={thStyle}>Category</th>
                        <th style={thStyle}>Action</th>
                        <th style={thStyle}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentBehaviors.logs.map((log) => (
                        <tr key={log.id}>
                          <td style={tdStyle}>{getRelativeTime(log.changedAt, timezone)}</td>
                          <td style={tdStyle}>
                            {log.studentId && log.studentFirstName && log.studentLastName ? (
                              <Link
                                to={`/admin/reports/student-progress?studentId=${log.studentId}`}
                                style={{ color: 'var(--primary-color)', textDecoration: 'none' }}
                              >
                                {formatStudentName(log.studentFirstName, log.studentLastName)}
                              </Link>
                            ) : (
                              <span style={{ color: 'var(--text-secondary)' }}>N/A</span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            {log.staffFirstName && log.staffLastName
                              ? formatStaffName(log.staffFirstName, log.staffLastName)
                              : <span style={{ color: 'var(--text-secondary)' }}>N/A</span>}
                          </td>
                          <td style={tdStyle}>
                            {log.categoryName ? (
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: log.categoryColor ? `${log.categoryColor}20` : '#e5e7eb',
                                color: log.categoryColor || '#374151',
                              }}>
                                {log.categoryName}
                              </span>
                            ) : <span style={{ color: 'var(--text-secondary)' }}>N/A</span>}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              backgroundColor: getActionColor(log.action) + '20',
                              color: getActionColor(log.action),
                            }}>
                              {log.action}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, ...truncatedTextStyle }} title={log.notesPreview || ''}>
                            {log.notesPreview || <span style={{ color: 'var(--text-secondary)' }}>-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <Link to="/admin/audit-logs?tab=behavior" style={linkButtonStyle}>
                  View All Behavior Records
                </Link>
              </div>
            )}

            {/* Recent Activity Panel (RIGHT) */}
            {hasActivity && (
              <div style={cardStyle}>
                <h2 style={cardTitleStyle}>Recent Activity</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                  Latest changes to students, classes, blocks, and other data
                </p>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Time</th>
                      <th style={thStyle}>Action</th>
                      <th style={thStyle}>Entity</th>
                      <th style={thStyle}>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.data.map((log) => (
                      <tr key={log.id}>
                        <td style={tdStyle}>{getRelativeTime(log.createdAt, timezone)}</td>
                        <td style={tdStyle}>
                          <span
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              backgroundColor: getActionColor(log.action) + '20',
                              color: getActionColor(log.action),
                            }}
                          >
                            {log.action}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <code style={{ fontSize: '0.75rem' }}>{log.entityType}</code>
                        </td>
                        <td style={tdStyle}>
                          {log.userName || <code style={{ fontSize: '0.75rem' }}>{log.userId.slice(0, 8)}</code>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Link to="/admin/audit-logs" style={linkButtonStyle}>
                  View All Audit Logs
                </Link>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default DashboardPage;
